import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import { ProductOption, productChoices } from '../../utils/batchProducts';

interface Props {
  batch: { batchId: string; productId?: number | string | null; productName?: string | null };
  onCancel: () => void;
  onDone: (message: string, to: { productId: number | string; productName: string }) => void;
}

/** Batch Details "Change product": POST /api/batches/:id/product (batch event only; no inventory/TTB). */
const ChangeProductModal: React.FC<Props> = ({ batch, onCancel, onDone }) => {
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productId, setProductId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch(`${API_BASE_URL}/api/products`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((d) => setProducts(Array.isArray(d) ? d : []))
      .catch(() => setProducts([]));
  }, []);

  const choices = productChoices(products, batch).filter((p) => String(p.id) !== String(batch.productId));

  const confirm = async () => {
    if (!productId) return setError('Choose the new product.');
    if (!reason.trim()) return setError('Enter a reason (e.g. Barrels blended into Barrel Aged Porter).');
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batch.batchId)}/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ productId, reason: reason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onDone(data.message || `${batch.batchId} product changed`, { productId: data.to?.productId, productName: data.to?.productName });
    } catch (err: any) {
      setError('Change failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facility-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="change-product-title" data-testid="change-product-modal">
      <div className="facility-modal">
        <h3 id="change-product-title">Change product of {batch.batchId}</h3>
        <p className="facility-modal__meta">
          Now: <strong>{batch.productName || 'Product'}</strong>
          <br />
          Volume, inventory and TTB numbers don't change. Packaging will offer the new product's package types.
        </p>
        <label htmlFor="change-product-select">Change product to</label>
        <select id="change-product-select" data-testid="change-product-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">Choose a product…</option>
          {choices.map((p) => (
            <option key={String(p.id)} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>
        <label htmlFor="change-product-reason">Reason (required)</label>
        <input
          id="change-product-reason"
          data-testid="change-product-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Barrels blended into Barrel Aged Porter"
        />
        {error && (
          <div className="facility-modal__error" role="alert" data-testid="change-product-error">
            {error}
          </div>
        )}
        <div className="facility-modal__actions">
          <button type="button" className="is-primary" data-testid="change-product-confirm" onClick={confirm} disabled={busy}>
            {busy ? 'Saving…' : 'Change product'}
          </button>
          <button type="button" className="is-secondary" data-testid="change-product-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeProductModal;
